import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const TOKEN_KEY = 'admin_token';

const adminClient = axios.create({ baseURL: API_URL });

adminClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
      localStorage.removeItem(TOKEN_KEY);
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

const login = async ({ email, password }) => {
  try {
    const { data } = await adminClient.post('/api/admin/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.response?.data?.error || 'Login failed' };
  }
};

const logout = () => localStorage.removeItem(TOKEN_KEY);
const isAuthenticated = () => !!localStorage.getItem(TOKEN_KEY);

const getOverview = () => adminClient.get('/api/admin/overview').then((r) => r.data);
const getSignups = (days = 30) =>
  adminClient.get('/api/admin/signups', { params: { days } }).then((r) => r.data);
const getCompanies = () => adminClient.get('/api/admin/companies').then((r) => r.data);
const getCompanyDetail = (companyId) =>
  adminClient.get(`/api/admin/companies/${companyId}`).then((r) => r.data);
const getCompanyRequests = (companyId, days = 7) =>
  adminClient.get(`/api/admin/companies/${companyId}/requests`, { params: { days } }).then((r) => r.data);
const deleteCompany = (companyId) =>
  adminClient.delete(`/api/admin/companies/${companyId}`).then((r) => r.data);
const getUsers = (limit = 200) =>
  adminClient.get('/api/admin/users', { params: { limit } }).then((r) => r.data);
const resendUserVerification = (userId) =>
  adminClient.post(`/api/admin/users/${userId}/resend-verification`).then((r) => r.data);
const getBilling = () => adminClient.get('/api/admin/billing').then((r) => r.data);
const resyncCompanyPayfast = (companyId) =>
  adminClient.post(`/api/admin/companies/${companyId}/resync-payfast`).then((r) => r.data);
const getRequests = (days = 7) =>
  adminClient.get('/api/admin/requests', { params: { days } }).then((r) => r.data);
const getErrors = (days = 7, klass = null) =>
  adminClient.get('/api/admin/errors', {
    params: { days, ...(klass ? { class: klass } : {}) },
  }).then((r) => r.data);
const getActivity = (limit = 50) =>
  adminClient.get('/api/admin/activity', { params: { limit } }).then((r) => r.data);

const adminService = {
  login,
  logout,
  isAuthenticated,
  getOverview,
  getSignups,
  getCompanies,
  getCompanyDetail,
  getCompanyRequests,
  deleteCompany,
  getUsers,
  resendUserVerification,
  getBilling,
  resyncCompanyPayfast,
  getRequests,
  getErrors,
  getActivity,
};

export default adminService;
